import { create } from "zustand";

export type ChartGenerationStatus = "pending" | "done" | "empty" | "failed";

export type ChartGenerationEntry = {
  chartId: string;
  chapterId: string;
  status: ChartGenerationStatus;
  result?: { imageUrl: string; title: string };
};

type ChartGenerationState = {
  generations: Record<string, ChartGenerationEntry>;
  start: (chartId: string, chapterId: string) => void;
  succeed: (chartId: string, result: { imageUrl: string; title: string }) => void;
  markEmpty: (chartId: string) => void;
  fail: (chartId: string) => void;
  clear: (chartId: string) => void;
};

const setStatus = (
  state: ChartGenerationState,
  chartId: string,
  status: ChartGenerationStatus,
  result?: { imageUrl: string; title: string }
): Partial<ChartGenerationState> => {
  const prev = state.generations[chartId];
  if (!prev) return {};
  return { generations: { ...state.generations, [chartId]: { ...prev, status, result } } };
};

export const useChartGenerationStore = create<ChartGenerationState>((set) => ({
  generations: {},
  start: (chartId, chapterId) =>
    set((state) => ({
      generations: { ...state.generations, [chartId]: { chartId, chapterId, status: "pending" } }
    })),
  succeed: (chartId, result) => set((state) => setStatus(state, chartId, "done", result)),
  markEmpty: (chartId) => set((state) => setStatus(state, chartId, "empty")),
  fail: (chartId) => set((state) => setStatus(state, chartId, "failed")),
  clear: (chartId) =>
    set((state) => {
      if (!state.generations[chartId]) return {};
      const next = { ...state.generations };
      delete next[chartId];
      return { generations: next };
    })
}));
